import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api";
import type { TeamCheckin } from "./types";

// ---------------------------------------------------------------------------
// Check-in async thunks
// ---------------------------------------------------------------------------

export const fetchTeamCheckins = createAsyncThunk(
    "teamFocusSession/fetchCheckins",
    async (teamId: string, { rejectWithValue }) => {
        try {
            const resp = await api.teamMode.listCheckins(teamId);
            // Backend returns { total, checkins } — normalise to array
            let data: TeamCheckin[] = [];
            if (Array.isArray(resp)) {
                data = resp;
            } else if (resp && typeof resp === "object" && "checkins" in resp) {
                data = resp.checkins || [];
            }
            return { teamId, data };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch checkins",
            );
        }
    },
);

export const createTeamCheckin = createAsyncThunk(
    "teamFocusSession/createCheckin",
    async (
        {
            teamId,
            data,
        }: {
            teamId: string;
            data: { title: string; checkinTime: string; is_public?: boolean };
        },
        { rejectWithValue },
    ) => {
        try {
            const created = await api.teamMode.createCheckin(teamId, data);
            return { teamId, checkin: created };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to create checkin",
            );
        }
    },
);

export const updateTeamCheckin = createAsyncThunk(
    "teamFocusSession/updateCheckin",
    async (
        {
            teamId,
            checkinId,
            data,
        }: {
            teamId: string;
            checkinId: string;
            data: Partial<{
                title: string;
                checkinTime: string;
                is_public?: boolean;
            }>;
        },
        { rejectWithValue },
    ) => {
        try {
            const updated = await api.teamMode.updateCheckin(teamId, checkinId, data);
            return { teamId, checkinId, updates: updated };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to update checkin",
            );
        }
    },
);

export const deleteTeamCheckin = createAsyncThunk(
    "teamFocusSession/deleteCheckin",
    async (
        { teamId, checkinId }: { teamId: string; checkinId: string },
        { rejectWithValue },
    ) => {
        try {
            await api.teamMode.deleteCheckin(teamId, checkinId);
            return { teamId, checkinId };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to delete checkin",
            );
        }
    },
);
